import React from "react";
import PropTypes from "prop-types";
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Avatar,
  Button,
  Divider,
  makeStyles,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  ProgressBar
} from "@fluentui/react-components";
import { FormattedMessage } from "react-intl";
import { DocumentArrowUp20Filled, Edit20Filled, ImageMultiple20Filled, People20Filled } from "@fluentui/react-icons";
import configs from "../../utils/configs";

const useStyles = makeStyles({
  content: {
    display: "flex",
    flexDirection: "column",
    gap: "var(--Font-size-500, 16px)",
    width: "300px"
  },
  contentHeader: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "var(--fontSizeBase100, 10px)"
  },
  contentHeaderProfile: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "var(--fontSizeBase100, 10px)"
  },
  contentHeaderAvatar: {
    backgroundColor: "var(--colorNeutralBackground1)"
  },
  contentHeaderName: {
    fontSize: "var(--fontSizeBase400, 16px)",
    fontStyle: "normal",
    fontWeight: "600",
    lineHeight: "var(--lineHeightBase300, 20px)",
    // Max two line, after that it will be truncated
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    lineClamp: 2,
    boxOrient: "vertical",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    "-webkit-line-clamp": 2,
    "-webkit-box-orient": "vertical",
    overflowWrap: "anywhere"
  },
  contentHeaderButton: {
    whiteSpace: "nowrap"
  },
  contentPlanLimits: {
    fontSize: "var(--fontSizeBase200, 12px)",
    display: "flex",
    flexDirection: "column",
    gap: "5px",
    marginBottom: "var(--spacingVerticalM, 12px)"
  },
  signOutButtonContainer: {
    marginTop: "var(--spacingVerticalL, 16px)",
    width: "100%",
    paddingLeft: "var(--spacingHorizontalL, 16px)",
    paddingRight: "var(--spacingHorizontalL, 16px)"
  },
  upgradeButton: {
    marginTop: "var(--spacingVerticalL, 16px)",
    width: "100%"
  },
  editNameButton: {
    marginLeft: "var(--spacingHorizontalMNudge, 10px)"
  }
});

function CCULabel({ ccuLimit }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "5px"
      }}
    >
      <People20Filled />
      <span>
        <FormattedMessage id="avatar-preview-popover.ccu-used" defaultMessage="{ccuLimit}" values={{ ccuLimit }} />
      </span>
    </div>
  );
}

CCULabel.propTypes = {
  ccuUsed: PropTypes.number,
  ccuLimit: PropTypes.number
};

function StorageLabel({ storageUsed, storageLimit }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "5px"
      }}
    >
      <DocumentArrowUp20Filled />
      <span>
        <FormattedMessage
          id="avatar-preview-popover.storage-used"
          defaultMessage="{storageUsed}/{storageLimit} MB"
          values={{ storageUsed, storageLimit }}
        />
      </span>
    </div>
  );
}

StorageLabel.propTypes = {
  storageUsed: PropTypes.number,
  storageLimit: PropTypes.number
};

function ScenesLabel({ scenesUsed, scenesLimit }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "5px"
      }}
    >
      <ImageMultiple20Filled />
      <span>
        <FormattedMessage
          id="avatar-preview-popover.scenes-used"
          defaultMessage="{scenesUsed}/{scenesLimit}"
          values={{ scenesUsed, scenesLimit }}
        />
      </span>
    </div>
  );
}

ScenesLabel.propTypes = {
  scenesUsed: PropTypes.number,
  scenesLimit: PropTypes.number
};

export function AvatarPreviewPopover({
  image,
  name,
  className,
  ccuLimit = 5,
  storageLimit = 512,
  ccuUsed = 3,
  storageUsed = 126,
  scenesLimit = 3,
  scenesUsed = 0,
  onEditAvatar,
  onEditName,
  onSignOut,
  open,
  setOpen,
  loadingSubscriptions,
  subscriptionName,
  subscriptionsEnabled
}) {
  const styles = useStyles();

  const handleEditAvatar = () => {
    setOpen(false);
    onEditAvatar();
  };

  const handleEditName = () => {
    onEditName();
  };

  return (
    <>
      <Popover
        className={className}
        positioning={"below-start"}
        withArrow={true}
        size="medium"
        open={open}
        onOpenChange={(_, data) => setOpen(data.open)}
      >
        <PopoverTrigger disableButtonEnhancement>
          <Avatar
            className={styles.contentHeaderAvatar}
            size={40}
            image={{
              src: image,
              alt: name
            }}
            color
          />
        </PopoverTrigger>
        <PopoverSurface className={styles.content} tabIndex={-1}>
          <div className={styles.contentHeader}>
            <div className={styles.contentHeaderProfile}>
              <Avatar
                className={styles.contentHeaderAvatar}
                size={48}
                image={{
                  src: image,
                  alt: name
                }}
              />
              <div style={{ display: "flex", alignItems: "center" }}>
                <p className={styles.contentHeaderName}>{name}</p>
                <Button
                  appearance="transparent"
                  size="small"
                  icon={<Edit20Filled />}
                  onClick={handleEditName}
                  className={styles.editNameButton}
                />
              </div>
            </div>
            <Button appearance="primary" size="small" className={styles.contentHeaderButton} onClick={handleEditAvatar}>
              <FormattedMessage id="object-menu.edit-avatar-button" defaultMessage="Edit Avatar" />
            </Button>
          </div>

          <Divider />
          {subscriptionsEnabled && (
            <>
              {loadingSubscriptions ? (
                <ProgressBar indeterminate />
              ) : (
                <Accordion collapsible defaultOpenItems={["1"]}>
                  <AccordionItem value="1">
                    <AccordionHeader>
                      <FormattedMessage
                        id="avatar-preview-popover.plan-limits"
                        defaultMessage="{subscriptionName}"
                        values={{ subscriptionName }}
                      />
                    </AccordionHeader>
                    <AccordionPanel>
                      <AccordionItem value="1">
                        <div className={styles.contentPlanLimits}>
                          <ProgressBar value={Math.min(scenesUsed / scenesLimit, 1)} />
                          <ScenesLabel scenesUsed={scenesUsed} scenesLimit={scenesLimit} />
                        </div>
                        <div className={styles.contentPlanLimits}>
                          <ProgressBar value={Math.min(storageUsed / storageLimit, 1)} />
                          <StorageLabel storageUsed={storageUsed} storageLimit={storageLimit} />
                        </div>
                        <div className={styles.contentPlanLimits}>
                          <CCULabel ccuUsed={ccuUsed} ccuLimit={ccuLimit} />
                        </div>
                        <Button
                          appearance="primary"
                          as="a"
                          href={configs.PRICING_PAGE_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.upgradeButton}
                        >
                          <FormattedMessage id="avatar-preview-popover.upgrade-now" defaultMessage="Upgrade now" />
                        </Button>
                      </AccordionItem>
                    </AccordionPanel>
                  </AccordionItem>
                </Accordion>
              )}
            </>
          )}

          <div className={styles.signOutButtonContainer}>
            <Button appearance="outline" onClick={onSignOut} style={{ width: "100%" }}>
              <FormattedMessage id="profile-menu.sign-out" defaultMessage="Sign Out" />
            </Button>
          </div>
        </PopoverSurface>
      </Popover>
    </>
  );
}

AvatarPreviewPopover.propTypes = {
  image: PropTypes.string,
  name: PropTypes.string,
  className: PropTypes.string,
  ccuLimit: PropTypes.number,
  storageLimit: PropTypes.number,
  ccuUsed: PropTypes.number,
  storageUsed: PropTypes.number,
  onEditAvatar: PropTypes.func,
  onSignOut: PropTypes.func,
  onEditName: PropTypes.func,
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  scenesLimit: PropTypes.number,
  scenesUsed: PropTypes.number,
  loadingSubscriptions: PropTypes.bool,
  subscriptionName: PropTypes.string,
  subscriptionsEnabled: PropTypes.bool
};
